import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined } from '@ant-design/icons';
import { AppBarCMS } from '../../../../basic';
import { CollapseBlock, Spinner } from '../../../../common';
import DataGrid from '../../../../common/DataGrid/DataGrid.jsx';
import { IconButton } from '../../../../common/controls/Controls.jsx';
import { routes } from '../../../../../utils/helpers/ApiClient';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import usePermission from '../../../../../hooks/usePermission.jsx';
import {
  useDeletePhdStudyFieldMutation,
  useDeletePhdStudySubjectMutation,
  useGetPhdStudyFieldsQuery,
  useGetPhdStudySubjectsQuery,
} from '../../../../../redux/services/phd';
import { useDataGridTextFilter } from '../../../../common/DataGrid/filters/useDataGridTextFilter.jsx';
import { renderPhdAbbreviatedValue } from '../utils/renderPhdAbbreviatedValue.jsx';
import PhdPageContext from '../../../../common/Phd/PhdPageContext.jsx';
import {
  normalizeCollection,
  resolveCompletionTypeLabel,
  resolveSubjectTypeLabel,
} from './phdStudyFieldsHelpers.js';

const EXPANDED_ROWS_STORAGE_KEY = 'phd-study-fields-expanded-rows';

const PhdStudyFieldsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const canCreateSubject = usePermission('phd-study-subjects.store');
  const pageTitle = t('Študijné odbory a predmety');

  useEffect(() => {
    document.title = `${pageTitle} - FRI CMS`;
  }, [pageTitle]);

  const { data: fields, isLoading: isFieldsLoading } =
    useGetPhdStudyFieldsQuery();
  const { data: subjects, isLoading: isSubjectsLoading } =
    useGetPhdStudySubjectsQuery();
  const [deleteField] = useDeletePhdStudyFieldMutation();
  const [deleteSubject] = useDeletePhdStudySubjectMutation();
  const { deleteSuccess, deleteFailed } = useToastMessages();

  const [expandedRowKeys, setExpandedRowKeys] = useState(() => {
    if (typeof window === 'undefined') {
      return [];
    }

    try {
      const stored = window.localStorage.getItem(EXPANDED_ROWS_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      return [];
    }
  });

  const fieldList = useMemo(() => normalizeCollection(fields), [fields]);
  const subjectList = useMemo(() => normalizeCollection(subjects), [subjects]);

  const subjectMap = useMemo(() => {
    return subjectList.reduce((acc, subject) => {
      if (subject?.id != null) {
        acc.set(subject.id, subject);
      }
      return acc;
    }, new Map());
  }, [subjectList]);

  const fieldRows = useMemo(
    () =>
      fieldList.map((field) => {
        const rawSubjects = Array.isArray(field?.subjects)
          ? field.subjects
          : [];

        const subjectRows = rawSubjects.map((subject) => {
          const mappedSubject = subjectMap.get(subject?.id);
          const mergedSubject = {
            ...subject,
            ...(mappedSubject || {}),
          };

          return {
            ...mergedSubject,
            subject_type_label: resolveSubjectTypeLabel(
              mergedSubject.subject_type,
              t,
            ),
            completion_type_label: resolveCompletionTypeLabel(
              mergedSubject.completion_type,
              t,
            ),
          };
        });

        return {
          ...field,
          subject_rows: subjectRows,
        };
      }),
    [fieldList, subjectMap, t],
  );

  const handleDeleteField = async (row) => {
    try {
      await deleteField(row.id).unwrap();
      deleteSuccess();
    } catch (error) {
      deleteFailed(error);
    }
  };

  const handleDeleteSubject = async (row) => {
    try {
      await deleteSubject(row.id).unwrap();
      deleteSuccess();
    } catch (error) {
      deleteFailed(error);
    }
  };

  const handleExpandedRowsChange = (newExpandedRowKeys) => {
    setExpandedRowKeys(newExpandedRowKeys);

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(
        EXPANDED_ROWS_STORAGE_KEY,
        JSON.stringify(newExpandedRowKeys),
      );
    }
  };

  const openCreateFieldPage = () => {
    navigate(routes.cmsPhdStudyFieldsCreate);
  };

  const openCreateSubjectPage = (field) => {
    navigate(`${routes.cmsPhdStudySubjectsCreate}?fieldId=${field.id}`);
  };

  const openEditFieldPage = (field) => {
    navigate(`${routes.cmsPhdStudyFieldsEdit}/${field.id}`);
  };

  const openEditSubjectModal = (subject) => {
    navigate(`${routes.cmsPhdStudySubjectsEdit}/${subject.id}`);
  };

  const mainActions = useMemo(
    () => [
      {
        title: t('Pridať študijný odbor'),
        icon: <PlusOutlined />,
        onClick: openCreateFieldPage,
        permission: 'phd-study-fields.store',
      },
    ],
    [t],
  );

  const fieldFilters = useDataGridTextFilter('study_field');
  const codeFilters = useDataGridTextFilter('code');
  const focusFilters = useDataGridTextFilter('focus');
  const fieldUpdatedByFilter = useDataGridTextFilter('updated_by_name');

  const fieldColumns = [
    {
      title: t('Kód'),
      dataIndex: 'code',
      key: 'code',
      width: 100,
      render: (value) => value || '—',
      ...codeFilters,
    },
    {
      title: t('Študijný program'),
      dataIndex: 'study_field',
      key: 'study_field',
      ellipsis: true,
      ...fieldFilters,
    },
    {
      title: t('Študijný odbor'),
      dataIndex: 'focus',
      key: 'focus',
      ellipsis: true,
      render: (value) => value || '—',
      ...focusFilters,
    },
    {
      title: t('Naposledy upravené'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 170,
      render: (value) =>
        value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '—',
    },
    {
      title: t('Upravil'),
      dataIndex: 'updated_by_name',
      key: 'updated_by_name',
      ellipsis: true,
      render: (value) => value || '—',
      ...fieldUpdatedByFilter,
    },
  ];

  const subjectNameFilter = useDataGridTextFilter('name');
  const subjectCodeFilter = useDataGridTextFilter('code');
  const subjectTypeFilter = useDataGridTextFilter('subject_type_label');
  const completionTypeFilter = useDataGridTextFilter('completion_type_label');
  const subjectUpdatedByFilter = useDataGridTextFilter('updated_by_name');

  const subjectColumns = [
    {
      title: t('Kód'),
      dataIndex: 'code',
      key: 'code',
      width: 120,
      ...subjectCodeFilter,
    },
    {
      title: t('Predmet'),
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      ...subjectNameFilter,
    },
    {
      title: t('Typ predmetu'),
      dataIndex: 'subject_type_label',
      key: 'subject_type_label',
      ellipsis: true,
      ...subjectTypeFilter,
    },
    {
      title: t('Typ ukončenia'),
      dataIndex: 'completion_type',
      key: 'completion_type',
      width: 140,
      render: (value, row) =>
        renderPhdAbbreviatedValue(value, row.completion_type_label),
      ...completionTypeFilter,
    },
    {
      title: t('Naposledy upravené'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 170,
      render: (value) =>
        value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '—',
    },
    {
      title: t('Upravil'),
      dataIndex: 'updated_by_name',
      key: 'updated_by_name',
      ellipsis: true,
      render: (value) => value || '—',
      ...subjectUpdatedByFilter,
    },
  ];

  if (isFieldsLoading || isSubjectsLoading) {
    return <Spinner />;
  }

  return (
    <>
      <AppBarCMS
        title={pageTitle}
        backUrl={routes.cmsPhdTheses}
        backUrlTooltip={t('Dizertačné témy')}
        mainActions={mainActions}
        permissionsBasic="phd-study-fields"
      />
      <div style={{ margin: '10px' }}>
        <PhdPageContext
          title={t('Ako fungujú študijné odbory a predmety?')}
          description={t(
            'Odbory a predmety tvoria spoločný číselník pre témy a individuálne plány. Najprv vytvorte odbor, potom mu priraďte predmety.',
          )}
        />
        <CollapseBlock title={t('Doktorandské študijné odbory')}>
          {fieldRows.length ? (
            <DataGrid
              locale={{
                emptyText: t(
                  'Pre zvolený pohľad a filtre nie sú dostupné žiadne záznamy.',
                ),
              }}
              rowKey={(row) => row.id}
              columns={fieldColumns}
              dataSource={fieldRows}
              permissionsBasic="phd-study-fields"
              uniqueTableId="phd-fields-table"
              onEditClick={openEditFieldPage}
              onRowClick={openEditFieldPage}
              onDelete={handleDeleteField}
              customSettings={(row) => (
                <div style={{ display: 'flex' }}>
                  <IconButton
                    icon={<PlusOutlined />}
                    tooltip={t('Pridať predmet')}
                    disabled={!canCreateSubject}
                    disabledTooltip={
                      !canCreateSubject
                        ? t('Nemáte oprávnenie pridávať predmety.')
                        : undefined
                    }
                    onClick={(event) => {
                      event.stopPropagation();
                      if (!canCreateSubject) {
                        return;
                      }
                      openCreateSubjectPage(row);
                    }}
                  />
                </div>
              )}
              disableExpandRow={(record) => !(record?.subject_rows?.length > 0)}
              customExpandedRowRender={(record) => (
                <DataGrid
                  locale={{
                    emptyText: t(
                      'Pre zvolený pohľad a filtre nie sú dostupné žiadne záznamy.',
                    ),
                  }}
                  rowKey={(row) => row.id}
                  columns={subjectColumns}
                  dataSource={record.subject_rows || []}
                  permissionsBasic="phd-study-subjects"
                  uniqueTableId={`phd-field-${record.id}-subjects-table`}
                  onEditClick={openEditSubjectModal}
                  onRowClick={openEditSubjectModal}
                  onDelete={handleDeleteSubject}
                  disableExpandRow={() => true}
                  pageSize={5}
                />
              )}
              expandedRowKeys={expandedRowKeys}
              onExpandedRowsChange={handleExpandedRowsChange}
            />
          ) : (
            <p>{t('Zatiaľ tu nie sú žiadne položky.')}</p>
          )}
        </CollapseBlock>
      </div>
    </>
  );
};

export default PhdStudyFieldsPage;
